import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ledgerApi } from "../lib/ledgerApi";
import HashString from "../components/HashString";
import { ArrowLeft, ArrowRight, ChevronRight } from "lucide-react";

const KV = ({ label, children, mono }) => (
  <div className="grid grid-cols-12 gap-3 py-3 border-b border-border last:border-0">
    <div className="col-span-12 sm:col-span-3 text-[10px] sm:text-xs uppercase tracking-[0.2em] text-muted-foreground self-center">
      {label}
    </div>
    <div className={`col-span-12 sm:col-span-9 ${mono ? "font-mono" : ""} text-sm break-all`}>
      {children}
    </div>
  </div>
);

export default function BlockDetail() {
  const { n } = useParams();
  const [block, setBlock] = useState(null);
  const [error, setError] = useState(null);
  const num = Number(n);

  useEffect(() => {
    let alive = true;
    setBlock(null);
    setError(null);
    ledgerApi
      .getBlock(num)
      .then((b) => alive && setBlock(b))
      .catch((e) =>
        alive &&
        setError(e?.response?.data?.detail || e.message || "failed to load block")
      );
    return () => {
      alive = false;
    };
  }, [num]);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-8 py-10 md:py-14">
      <div className="mb-6 flex items-center justify-between gap-2 flex-wrap">
        <Link to="/blocks" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="back-to-blocks">
          <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> All blocks
        </Link>
        <div className="flex items-center gap-2">
          {num > 0 && (
            <Link to={`/blocks/${num - 1}`} className="px-2.5 py-1 border border-border rounded-sm text-xs hover:border-foreground inline-flex items-center gap-1" data-testid="block-prev">
              <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> #{num - 1}
            </Link>
          )}
          <Link to={`/blocks/${num + 1}`} className="px-2.5 py-1 border border-border rounded-sm text-xs hover:border-foreground inline-flex items-center gap-1" data-testid="block-next">
            #{num + 1} <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
          </Link>
        </div>
      </div>

      <div className="mb-2 text-[10px] uppercase tracking-[0.25em] text-muted-foreground">/ Block</div>
      <h1 className="text-4xl sm:text-5xl tracking-tight font-medium font-mono mb-8" data-testid="block-detail-title">
        #{num}
      </h1>

      {error && (
        <div className="border border-status-tampered/40 bg-status-tampered/10 p-4 rounded-sm text-status-tampered text-sm">
          {error}
        </div>
      )}
      {!block && !error && <div className="text-sm text-muted-foreground">Loading…</div>}

      {block && (
        <>
          <div className="border border-border rounded-sm bg-card p-4 sm:p-6">
            <KV label="Block number" mono>{block.block_number}</KV>
            <KV label="Block hash" mono>
              <HashString value={block.block_hash} label="Block hash" testid="block-detail-hash" />
            </KV>
            <KV label="Previous hash" mono>
              {block.block_number > 0 ? (
                <Link to={`/blocks/${block.block_number - 1}`} className="hover:underline">
                  <HashString value={block.previous_hash} label="Previous hash" />
                </Link>
              ) : (
                <span className="text-muted-foreground">{block.previous_hash} <span className="ml-2 text-[10px] uppercase tracking-[0.2em]">genesis</span></span>
              )}
            </KV>
            <KV label="Merkle root" mono>
              <HashString value={block.merkle_root} label="Merkle root" />
            </KV>
            <KV label="Timestamp" mono>{new Date(block.timestamp).toLocaleString()}</KV>
            <KV label="Nonce" mono>{block.nonce}</KV>
            <KV label="Tx count" mono>{block.tx_count}</KV>
            {block.legacy_single_sealer ? (
              <KV label="Federation" mono>
                <span data-testid="block-legacy-badge" className="px-1.5 py-0.5 border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 rounded-sm text-[10px] uppercase tracking-widest">
                  legacy single-sealer
                </span>
                <span className="ml-2 text-[11px] text-muted-foreground normal-case">pre-Phase-6 block · validator sigs not applicable</span>
              </KV>
            ) : (
              <>
                <KV label="Proposer" mono>
                  {block.proposer_name || block.proposer_id || "—"}
                </KV>
                <KV label="Consensus" mono>
                  <span data-testid="block-consensus-weight">
                    {block.consensus_valid_weight ?? "—"} / {block.consensus_threshold ?? "—"} valid weight
                  </span>
                  {block.byzantine_detected && (
                    <span className="ml-2 px-1.5 py-0.5 border border-status-tampered/40 bg-status-tampered/10 text-status-tampered rounded-sm text-[10px] uppercase tracking-widest">
                      byzantine detected
                    </span>
                  )}
                </KV>
                {(block.validator_signatures || []).length > 0 && (
                  <KV label="Validators" mono>
                    <div data-testid="block-validator-sigs" className="border border-border rounded-sm divide-y divide-border bg-background">
                      {block.validator_signatures.map((s, i) => (
                        <div key={i} className="px-3 py-2 grid grid-cols-12 gap-2 text-[11px] items-center">
                          <div className="col-span-3">{s.validator_name}</div>
                          <div className="col-span-6 text-[10px] text-muted-foreground truncate">
                            {s.sig?.slice(0, 32) || "—"}{s.sig?.length > 32 ? "…" : ""}
                          </div>
                          <div className="col-span-1 text-center">w{s.weight ?? 1}</div>
                          <div className="col-span-2 text-right">
                            {s.byzantine_flag ? (
                              <span className="px-1 py-0.5 border border-status-tampered/40 bg-status-tampered/10 text-status-tampered rounded-sm text-[9px] uppercase tracking-widest">byz</span>
                            ) : s.valid ? (
                              <span className="px-1 py-0.5 border border-status-ok/40 bg-status-ok/10 text-status-ok rounded-sm text-[9px] uppercase tracking-widest">valid</span>
                            ) : (
                              <span className="px-1 py-0.5 border border-muted-foreground/30 bg-muted text-muted-foreground rounded-sm text-[9px] uppercase tracking-widest">missed</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </KV>
                )}
              </>
            )}
          </div>

          <h2 className="text-lg font-medium mt-10 mb-3">Transactions</h2>
          {(() => {
            const groups = new Map();
            const singles = [];
            for (const tx of block.transactions) {
              if (tx.bundle_id) {
                if (!groups.has(tx.bundle_id)) groups.set(tx.bundle_id, []);
                groups.get(tx.bundle_id).push(tx);
              } else {
                singles.push(tx);
              }
            }
            return (
              <div className="space-y-4">
                {[...groups.entries()].map(([bid, legs]) => (
                  <div key={bid} data-testid={`bundle-${bid}`} className="border border-status-info/40 rounded-sm bg-status-info/5">
                    <div className="px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-status-info border-b border-status-info/30 flex items-center justify-between gap-2 flex-wrap">
                      <span>Atomic bundle · {legs.length} legs</span>
                      <HashString value={bid} label="Bundle id" />
                    </div>
                    <div className="divide-y divide-border">
                      {legs.map((tx) => (
                        <Link to={`/tx/${tx.tx_id}`} key={tx.tx_id} data-testid={`tx-row-${tx.tx_id}`} className="p-4 grid grid-cols-12 gap-3 items-center hover:bg-muted/40 transition-colors">
                          <div className="col-span-12 sm:col-span-2">
                            <span className="px-2 py-0.5 border border-status-info/40 bg-status-info/10 text-status-info text-[10px] uppercase tracking-widest rounded-sm font-mono">{tx.bundle_role}</span>
                          </div>
                          <div className="col-span-12 sm:col-span-5 min-w-0"><HashString value={tx.tx_id} label="Tx id" /></div>
                          <div className="col-span-7 sm:col-span-3 min-w-0 text-xs font-mono text-muted-foreground">
                            <div className="truncate">to {tx.to_address.slice(0, 18)}…</div>
                          </div>
                          <div className="col-span-5 sm:col-span-2 text-right font-mono">
                            <div className="text-sm">{tx.amount} <span className="text-muted-foreground">{tx.currency}</span></div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
                {singles.length > 0 && (
                  <div className="border border-border rounded-sm bg-card divide-y divide-border">
                    {singles.map((tx) => (
                      <Link to={`/tx/${tx.tx_id}`} key={tx.tx_id} data-testid={`tx-row-${tx.tx_id}`} className="p-4 grid grid-cols-12 gap-3 items-center hover:bg-muted/40 transition-colors">
                        <div className="col-span-12 sm:col-span-5 min-w-0">
                          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">{tx.kind === "coinbase" ? "Coinbase" : "Transfer"}</div>
                          <HashString value={tx.tx_id} label="Tx id" />
                        </div>
                        <div className="col-span-7 sm:col-span-5 min-w-0 text-xs font-mono text-muted-foreground">
                          <div className="truncate">from <span className="text-foreground">{tx.from_address}</span></div>
                          <div className="truncate">to <span className="text-foreground">{tx.to_address}</span></div>
                        </div>
                        <div className="col-span-4 sm:col-span-2 text-right font-mono">
                          <div className="text-sm">{tx.amount} <span className="text-muted-foreground">{tx.currency}</span></div>
                          <ChevronRight className="w-4 h-4 inline text-muted-foreground" strokeWidth={1.5} />
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}
